import { useEffect, useEffectEvent, useRef, useState, type ReactNode } from 'react';
import { ancillaryCabinOf } from '@voll-intelligence/domain';
import { useGenUiI18n, type GenUiTranslate } from '../../shared/ui/i18n.js';
import { formatCurrency } from '@voll-intelligence/intelligence-ui-kit/checkout';
import type { FlightLeg } from './flight_offers_port.js';
import {
  isFlightSeatMapUnavailable,
  useFlightSeatMapPort,
  type FlightSeatMapQuery,
  type FlightSeatMapView,
} from './flight_seat_map_port.js';
import { FlightSeatMapGrid } from './flight_seat_map_grid.js';
import './flight_seat_map.css';

/** `unavailable` = o fornecedor não produz o mapa (definitivo, #5248);
 *  `error` = falha recuperável com "Tentar de novo"; `gaveUp` = a falha
 *  recuperável se repetiu `MAX_FAILURES` vezes seguidas — a strip para de
 *  insistir e vira aviso definitivo (rede de segurança contra o laço). */
type SeatMapState =
  | { phase: 'loading' }
  | { phase: 'ready'; view: FlightSeatMapView | null }
  | { phase: 'error' }
  | { phase: 'unavailable' }
  | { phase: 'gaveUp' };

/** A abertura + UM "Tentar de novo": a segunda falha seguida já é definitiva. */
const MAX_FAILURES = 2;

interface EligibleLeg {
  leg: FlightLeg;
  key: string;
  query: FlightSeatMapQuery;
}

/** Identidade completa do voo ou nada — perna de sessão legada (sem cia/nº/data)
 *  fica de fora, não tem o que consultar no Sabre. Perna codeshare (a timeline
 *  já mostra "Operado por Avianca (AV)") leva a cia OPERADORA junto — sem ela o
 *  Sabre recebe a cia marketing em <Operating> e nega o mapa (issue #2296). A
 *  cabine da tarifa ("EXECUTIVE" do fornecedor) vai no vocabulário do domínio —
 *  sem ela o mapa é sempre o da econômica (issue #5949). */
function toQuery(leg: FlightLeg): FlightSeatMapQuery | null {
  const departureDate = leg.origin.at?.slice(0, 10);
  if (!leg.carrierCode || !leg.flightNumber || !departureDate) return null;
  const operatingCarrier =
    leg.operatedBy?.code && leg.operatedBy.code !== leg.carrierCode
      ? leg.operatedBy.code
      : undefined;
  const cabin = ancillaryCabinOf(leg.cabin);
  return {
    carrier: leg.carrierCode,
    flightNumber: leg.flightNumber,
    departureDate,
    origin: leg.origin.iata,
    destination: leg.destination.iata,
    ...(operatingCarrier ? { operatingCarrier } : {}),
    ...(cabin ? { cabin } : {}),
  };
}

/** Chave estável por identidade de voo (não posição na lista) — reordenar ou
 *  substituir a perna num mesmo índice não deve herdar fetch/estado de outro
 *  voo. Inclui a identidade operadora: a mesma perna com e sem `operatedBy`
 *  hidratado são consultas distintas. E a cabine: o mapa da econômica não serve
 *  a executiva do mesmo voo (issue #5949). */
function queryKey(q: FlightSeatMapQuery): string {
  return [
    q.carrier,
    q.flightNumber,
    q.departureDate,
    q.origin,
    q.destination,
    q.operatingCarrier ?? '',
    q.operatingFlightNumber ?? '',
    q.cabin ?? '',
  ].join('|');
}

/** "GRU → GIG" — rota curta usada no rótulo mono da strip e no aria de abertura
 *  do painel, e no sub-cabeçalho do painel. */
function routeLabel(q: FlightSeatMapQuery): string {
  return `${q.origin} → ${q.destination}`;
}

/** Linha de disponibilidade da strip, composta das MESMAS chaves i18n do resumo
 *  da grade (`FlightSeatMapGrid`) — a grade fica intocada, então a composição é
 *  repetida aqui (só a parte relevante numa linha curta: contagem de livres +
 *  "a partir de" quando há assento pago). */
function stripAvailabilityLabel(
  view: FlightSeatMapView,
  t: GenUiTranslate,
  locale: string,
): string {
  const available =
    view.summary.availableSeats === 1
      ? t('flightSeatMap.available_one')
      : t('flightSeatMap.available_other', { count: view.summary.availableSeats });
  const paidFrom = view.summary.minPaid
    ? t('flightSeatMap.paidFrom', {
        price: formatCurrency(
          Number(view.summary.minPaid.amount),
          view.summary.minPaid.currency,
          locale,
        ),
      })
    : undefined;
  return [available, paidFrom].filter((part): part is string => Boolean(part)).join(' · ');
}

/** k de 6 pontinhos "acesos" — densidade grosseira de disponibilidade
 *  (k = round(6 × livres/total)), só decoração visual ao lado do resumo textual
 *  (que já carrega a informação real para leitor de tela). */
function litDots(summary: FlightSeatMapView['summary']): number {
  if (summary.totalSeats <= 0) return 0;
  return Math.round(6 * (summary.availableSeats / summary.totalSeats));
}

/** Conteúdo à direita da strip: nada enquanto carrega (só o caret); pontinhos +
 *  resumo quando a grade está pronta; aviso mudo e definitivo quando pronta sem
 *  grade (null, ausência REAL de mapa), quando o fornecedor não produz o mapa
 *  (`unavailable`, #5248) e quando a falha recuperável se repetiu (`gaveUp`);
 *  erro = falha recuperável (rede, issue #3099) com "Tentar de novo" — a strip
 *  inteira vira o botão de retry. */
function StripInfo({
  state,
  t,
  locale,
}: {
  readonly state: SeatMapState;
  readonly t: GenUiTranslate;
  readonly locale: string;
}): ReactNode {
  if (state.phase === 'ready' && state.view) {
    const k = litDots(state.view.summary);
    return (
      <>
        <span className="fsm-strip-dots" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <span key={i} className={`fsm-strip-dot${i < k ? ' is-on' : ''}`} />
          ))}
        </span>
        <span>{stripAvailabilityLabel(state.view, t, locale)}</span>
      </>
    );
  }
  if (state.phase === 'ready' && state.view === null) {
    return <span>{t('flightSeatMap.unavailable')}</span>;
  }
  if (state.phase === 'unavailable') return <span>{t('flightSeatMap.supplierUnavailable')}</span>;
  if (state.phase === 'gaveUp') return <span>{t('flightSeatMap.errorFinal')}</span>;
  if (state.phase === 'error') {
    return (
      <>
        <span>{t('flightSeatMap.error')}</span>
        <span className="fsm-strip-retry">{t('flightSeatMap.retry')}</span>
      </>
    );
  }
  return null; // 'loading' — só o caret aparece à direita.
}

/** Rótulo acessível da strip: o aviso definitivo (sem mapa, fornecedor, limite
 *  de tentativas) é o NOME do botão desabilitado — sem isso o leitor de tela
 *  ouviria "Ver mapa de assentos" num botão que não abre nada. */
function stripAria(state: SeatMapState, route: string, t: GenUiTranslate): string {
  if (state.phase === 'error') return t('flightSeatMap.stripRetryAria', { route });
  const final =
    (state.phase === 'ready' && state.view === null) ||
    state.phase === 'unavailable' ||
    state.phase === 'gaveUp';
  return t(final ? 'flightSeatMap.stripUnavailableAria' : 'flightSeatMap.stripOpenAria', {
    route,
  });
}

/** Uma strip por perna elegível: linha inteira clicável — abre o painel quando
 *  a grade está pronta, refaz a consulta quando deu erro; os avisos definitivos
 *  e o carregando ficam desabilitados (o aviso já está ali mesmo). */
function FlightSeatMapStrip({
  query,
  state,
  onOpen,
  onRetry,
  t,
  locale,
}: {
  readonly query: FlightSeatMapQuery;
  readonly state: SeatMapState;
  readonly onOpen: (query: FlightSeatMapQuery, view: FlightSeatMapView) => void;
  readonly onRetry: (query: FlightSeatMapQuery) => void;
  readonly t: GenUiTranslate;
  readonly locale: string;
}): ReactNode {
  const route = routeLabel(query);
  const readyView = state.phase === 'ready' ? state.view : null;
  const failed = state.phase === 'error';
  return (
    <button
      type="button"
      className="fsm-strip"
      disabled={!readyView && !failed}
      aria-label={stripAria(state, route, t)}
      onClick={() => {
        if (failed) onRetry(query);
        else if (readyView) onOpen(query, readyView);
      }}
    >
      <b>{t('flightSeatMap.stripLabel')}</b>
      <span className="fsm-strip-leg">{route}</span>
      <span className="fsm-strip-info">
        <StripInfo state={state} t={t} locale={locale} />
      </span>
      <span className="fsm-strip-caret" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

/**
 * Seção "Mapa de assentos" do drawer de detalhe: uma strip por perna com
 * identidade completa de voo (cia + nº + data — pernas de sessão legada ficam de
 * fora). TODAS as pernas elegíveis buscam a grade em paralelo já na montagem
 * (`getFlightSeatMapPort().getSeatMap`), sem gesto do usuário. Tocar numa strip
 * pronta chama `onOpen(query, view)` — o container usa isso pra trocar o corpo
 * do drawer pelo painel cheio (`FlightSeatMapPanel`, exportado deste módulo).
 * Sem port injetado ou sem nenhuma perna elegível, a seção some.
 */
export function FlightSeatMapSection({
  legs,
  onOpen,
}: {
  readonly legs: readonly FlightLeg[];
  readonly onOpen: (query: FlightSeatMapQuery, view: FlightSeatMapView) => void;
}): ReactNode | null {
  const port = useFlightSeatMapPort();
  const { t, locale } = useGenUiI18n();
  const [states, setStates] = useState<Record<string, SeatMapState>>({});
  const mountedRef = useRef(true);
  const fetchedRef = useRef<Set<string>>(new Set());
  // Falhas recuperáveis SEGUIDAS por identidade de voo — o retry só é oferecido
  // até `MAX_FAILURES`; uma resposta (mapa, null ou indisponível) zera.
  const failuresRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const eligible: EligibleLeg[] = legs
    .map((leg) => {
      const query = toQuery(leg);
      return query ? { leg, key: queryKey(query), query } : null;
    })
    .filter((e): e is EligibleLeg => e !== null);

  // Uma consulta por identidade de voo: `fetchedRef` (não o estado assíncrono)
  // guarda contra o fetch duplicado sob StrictMode (efeito monta → desmonta →
  // monta de novo, síncrono, antes de qualquer promise resolver). O retry
  // (strip em erro) solta a chave e consulta de novo.
  const load = (key: string, query: FlightSeatMapQuery, force = false): void => {
    if (!port || (fetchedRef.current.has(key) && !force)) return;
    fetchedRef.current.add(key);
    setStates((s) => ({ ...s, [key]: { phase: 'loading' } }));
    void port.getSeatMap(query).then(
      (result) => {
        failuresRef.current.delete(key);
        const next: SeatMapState = isFlightSeatMapUnavailable(result)
          ? { phase: 'unavailable' }
          : { phase: 'ready', view: result };
        if (mountedRef.current) setStates((s) => ({ ...s, [key]: next }));
      },
      () => {
        const failures = (failuresRef.current.get(key) ?? 0) + 1;
        failuresRef.current.set(key, failures);
        const next: SeatMapState = { phase: failures >= MAX_FAILURES ? 'gaveUp' : 'error' };
        if (mountedRef.current) setStates((s) => ({ ...s, [key]: next }));
      },
    );
  };

  // Fetch eager e em paralelo de TODAS as pernas elegíveis já na montagem, e de
  // cada perna que ficar elegível depois (outro voo, o port que chegou). Roda a
  // cada commit: `eligible`/`load` são recomputados a cada render e o Effect Event
  // lê os da vez; `fetchedRef` faz cada identidade de voo consultar uma vez só.
  const loadEligible = useEffectEvent(() => {
    for (const { key, query } of eligible) load(key, query);
  });
  useEffect(() => {
    loadEligible();
  });

  if (!port || eligible.length === 0) return null;

  return (
    <section className="fsm-section">
      {eligible.map(({ key, query }) => (
        <FlightSeatMapStrip
          key={key}
          query={query}
          state={states[key] ?? { phase: 'loading' }}
          onOpen={onOpen}
          onRetry={(q) => {
            load(key, q, true);
          }}
          t={t}
          locale={locale}
        />
      ))}
    </section>
  );
}

/**
 * Painel cheio "Mapa de assentos": sub-cabeçalho (cia + nº + rota, com aeronave
 * e cabine quando presentes) seguido da MESMA grade informativa
 * (`FlightSeatMapGrid` já traz resumo, legenda, fuselagem e aviso — não
 * duplicados aqui). Aberto pelo container ao tocar numa strip pronta; ocupa o
 * corpo inteiro do drawer no lugar da lista de strips.
 */
export function FlightSeatMapPanel({
  query,
  view,
}: {
  readonly query: FlightSeatMapQuery;
  readonly view: FlightSeatMapView;
}): ReactNode {
  const flightNo = [query.carrier, query.flightNumber].filter(Boolean).join(' ');
  const route = routeLabel(query);
  return (
    <div className="fsm-panel">
      <p className="fsm-panel-sub">
        <b>
          {flightNo} · {route}
        </b>
        {view.aircraft && <span> · {view.aircraft}</span>}
        {view.cabinClass && <span> · {view.cabinClass}</span>}
      </p>
      <div className="fsm-panel-grid">
        <FlightSeatMapGrid view={view} />
      </div>
    </div>
  );
}
